import React, { useRef, useEffect, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

import { deviceCategories } from '../common/deviceCategories';
import { t } from '../common/LocalizationProvider';
import { useAttributePreference } from '../common/preferences';
import palette from '../theme/palette';

const mapContainer = document.createElement('div');
mapContainer.style.width = '100%';
mapContainer.style.height = '100%';
let mapInstanceShared = null;

const ZoomControl = ({ map, googleMapsApi }) => {
    useEffect(() => {
        if (!map || !googleMapsApi) return;

        const controlDiv = document.createElement('div');
        controlDiv.style.margin = '10px';
        controlDiv.style.borderRadius = '3px';
        controlDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        controlDiv.style.cursor = 'pointer';
        controlDiv.style.backgroundColor = '#fff';
        controlDiv.style.textAlign = 'center';

        const buttonStyle = `
            display: block;
            width: 30px;
            height: 30px;
            line-height: 30px;
            font-size: 22px;
            font-family: 'Roboto', 'Arial', sans-serif;
            font-weight: 500;
            color: #333;
            user-select: none;
        `;

        const zoomInButton = document.createElement('div');
        zoomInButton.innerHTML = '+';
        zoomInButton.style.cssText = buttonStyle;
        controlDiv.appendChild(zoomInButton);

        const separator = document.createElement('div');
        separator.style.borderTop = '1px solid #ccc';
        controlDiv.appendChild(separator);

        const zoomOutButton = document.createElement('div');
        zoomOutButton.innerHTML = '−';
        zoomOutButton.style.cssText = buttonStyle;
        controlDiv.appendChild(zoomOutButton);

        zoomInButton.addEventListener('click', () => {
            map.setZoom(map.getZoom() + 1);
        });

        zoomOutButton.addEventListener('click', () => {
            map.setZoom(map.getZoom() - 1);
        });

        map.controls[googleMapsApi.ControlPosition.TOP_RIGHT].push(controlDiv);

        return () => {
            const controls = map.controls[googleMapsApi.ControlPosition.TOP_RIGHT];
            for (let i = 0; i < controls.getLength(); i++) {
                if (controls.getAt(i) === controlDiv) {
                    controls.removeAt(i);
                    break;
                }
            }
        };

    }, [map, googleMapsApi]);

    return null;
};

const loadImageElement = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(new Error(`Failed to load image: ${url} ${err.type || 'error'}`));
        img.src = url;
    });
};

const prepareGoogleIcon = async (googleMapsApi, backgroundUrl, iconUrl, tintColor, baseWidth = 32, baseHeight = 32) => {
    if (!googleMapsApi) {
        console.warn('prepareGoogleIcon called before Google Maps API is ready.');
        return iconUrl;
    }
    const dpr = window.devicePixelRatio || 1;
    const canvas = document.createElement('canvas');
    canvas.width = baseWidth * dpr;
    canvas.height = baseHeight * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        console.error("Could not get canvas context");
        return iconUrl;
    }
    ctx.scale(dpr, dpr);

    try {
        if (backgroundUrl) {
            const backgroundImg = await loadImageElement(backgroundUrl);
            ctx.drawImage(backgroundImg, 0, 0, baseWidth, baseHeight);
        }

        if (iconUrl) {
            const iconImg = await loadImageElement(iconUrl);
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = baseWidth * 2;
            tempCanvas.height = baseHeight * 2;
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) {
                console.error("Could not get temp canvas context for tinting");
                ctx.drawImage(iconImg, 5, 5, baseWidth, baseHeight);
            } else {
                tempCtx.drawImage(iconImg, 5, 5, baseWidth, baseHeight);
                if (tintColor) {
                    tempCtx.globalCompositeOperation = 'source-in';
                    tempCtx.fillStyle = tintColor;
                    tempCtx.fillRect(5, 5, baseWidth, baseHeight);
                }
                ctx.drawImage(tempCanvas, 5, 5, baseWidth, baseHeight);
            }
        }
        return canvas.toDataURL();
    } catch (error) {
        console.error('Error preparing Google icon:', error);
        return iconUrl;
    }
};

const SwitcherControl = ({ map, googleMapsApi, mapTypes, initialMapTypeId, onStyleChange }) => {
    useEffect(() => {
        if (!map || !googleMapsApi || !mapTypes || mapTypes.length === 0) return;

        const controlDiv = document.createElement('div');
        controlDiv.style.margin = '10px';
        controlDiv.style.backgroundColor = '#fff';
        controlDiv.style.border = '1px solid #ccc';
        controlDiv.style.borderRadius = '3px';
        controlDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        controlDiv.style.cursor = 'pointer';
        controlDiv.style.textAlign = 'center';
        controlDiv.className = 'custom-map-control-button';

        const select = document.createElement('select');
        select.style.padding = '5px';
        select.style.border = 'none';
        select.style.fontSize = '14px';

        mapTypes.forEach(type => {
            const option = document.createElement('option');
            option.value = type.id;
            option.text = type.title;
            if (type.id === initialMapTypeId) {
                option.selected = true;
            }
            select.appendChild(option);
        });

        select.addEventListener('change', (event) => {
            const newMapTypeId = event.target.value;
            if (onStyleChange) onStyleChange(true);

            const mapType = mapTypes.find(mt => mt.id === newMapTypeId);
            if (mapType) {
                if (mapType.isCustomLayer) {
                    console.warn(`Custom layer ${newMapTypeId} not fully implemented in this example.`);
                    map.setMapTypeId(googleMapsApi.MapTypeId.ROADMAP);
                } else {
                    map.setMapTypeId(newMapTypeId);
                }
            }

            const idleListener = googleMapsApi.event.addListenerOnce(map, 'idle', () => {
                if (onStyleChange) onStyleChange(false);
            });
        });

        controlDiv.appendChild(select);
        map.controls[googleMapsApi.ControlPosition.TOP_RIGHT].push(controlDiv);

        return () => {
            // Basic cleanup: remove the control wrapper
            // This might need to be more robust if multiple controls are at this position
            const controls = map.controls[googleMapsApi.ControlPosition.TOP_RIGHT];
            for (let i = 0; i < controls.getLength(); i++) {
                if (controls.getAt(i) === controlDiv) {
                    controls.removeAt(i);
                    break;
                }
            }
        };
    }, [map, googleMapsApi, mapTypes, initialMapTypeId, onStyleChange]); // Add googleMapsApi to dependencies

    return null;
};


const MapGoogle = ({ children, onMapReady }) => {
    const mapContainerRef = useRef(null);

    const [googleApi, setGoogleApi] = useState(null);
    const [areIconsReady, setAreIconsReady] = useState(false);
    const [preparedIcons, setPreparedIcons] = useState({});
    const [isSwitchingStyle, setIsSwitchingStyle] = useState(false);

    const googleMapsApiKey = useAttributePreference('googleMapsApiKey');

    // 1. Load Google Maps API
    useEffect(() => {
        if (!googleMapsApiKey) {
            console.error("Google Maps API key is missing. لا يوجد مفتاح API");
            return;
        }
        if (googleApi) return;

        const loader = new Loader({
            apiKey: googleMapsApiKey,
            version: 'weekly',
            libraries: ['marker', 'maps', 'places'],
        });

        loader.load()
            .then((loadedGoogle) => {setGoogleApi(loadedGoogle);})
            .catch(e => {console.error("Failed to load Google Maps API:", e);});
    }, [googleMapsApiKey, googleApi]);

    useEffect(() => {
        const parentEl = mapContainerRef.current;
        if (parentEl) {
            parentEl.appendChild(mapContainer);
            return () => parentEl.removeChild(mapContainer);
        }
    }, [mapContainerRef]);

    // 2. Initialize map instance once API is loaded
    useEffect(() => {
        if (googleApi && !mapInstanceShared) {
            mapInstanceShared = new googleApi.maps.Map(mapContainer, {
                center: { lat: 0, lng: 0 },
                zoom: 3,
                mapTypeId: googleApi.maps.MapTypeId.ROADMAP,
                // disableDefaultUI: true,
                // zoomControl: true,
                // mapTypeControl: false, // We add a custom one
                // streetViewControl: false,
            });
        }
        if (onMapReady && googleApi)
            onMapReady({googleMap: mapInstanceShared, googleMapsApi: googleApi.maps})
    }, [googleApi, onMapReady, mapInstanceShared]);

    // 3. Prepare custom icons once Google API is ready
    useEffect(() => {
        if (!googleApi || areIconsReady) return;

        const initIcons = async () => {
            console.log('Preparing Google Maps icons...');
            const backgroundSvgUrl = 'images/background.svg';
            const iconsToLoad = {};

            for (const category of deviceCategories) {
                for (const colorKey of ['green', 'gray', 'red', 'yellow']) {
                    const iconSvgUrl = `images/icon/${category}.svg`;
                    const tint = palette.common[colorKey];
                    try {
                        const dataUrl = await prepareGoogleIcon(googleApi.maps, backgroundSvgUrl, iconSvgUrl, tint);
                        iconsToLoad[`${category}-${colorKey}`] = {
                            url: dataUrl,
                            scaledSize: new googleApi.maps.Size(48, 48),
                            anchor: new googleApi.maps.Point(16, 32),
                        };
                    } catch (error) {
                        console.error(`Failed to prepare icon ${category}-${colorKey}:`, error);
                        iconsToLoad[`${category}-${colorKey}`] = {
                            url: iconSvgUrl,
                            scaledSize: new googleApi.maps.Size(32, 32),
                            anchor: new googleApi.maps.Point(16, 32),
                        };
                    }
                }
            }
            setPreparedIcons(iconsToLoad);
            setAreIconsReady(true);
            console.log('Google Maps icons prepared.');
        };

        initIcons();
    }, [googleApi, areIconsReady]);

    const mapTypesForSwitcher = googleApi ? [
        { title: t('mapOsm'), id: 'osm_custom', isCustomLayer: true },
        { title: t('mapCarto'), id: 'carto_custom', isCustomLayer: true },
        { title: t('mapMapboxStreets'), id: googleApi.maps.MapTypeId.ROADMAP, titleOriginal: t('mapMapboxStreets') },
        { title: t('mapMapboxOutdoors'), id: googleApi.maps.MapTypeId.TERRAIN, titleOriginal: t('mapMapboxOutdoors') },
        { title: t('mapMapboxSatellite'), id: googleApi.maps.MapTypeId.SATELLITE, titleOriginal: t('mapMapboxSatellite') },
        { title: t('mapHybrid'), id: googleApi.maps.MapTypeId.HYBRID },
    ].map(mt => ({ ...mt, title: mt.titleOriginal || mt.title || mt.id })) : [];

    const overallReady = googleApi && areIconsReady && !isSwitchingStyle;

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

            {googleApi && mapInstanceShared && (
                <ZoomControl map={mapInstanceShared} googleMapsApi={googleApi.maps} />
            )}

            {/*{googleApi && mapInstance && mapTypesForSwitcher.length > 0 && ( // Ensure mapTypes are ready*/}
            {/*    <SwitcherControl*/}
            {/*        map={mapInstance}*/}
            {/*        googleMapsApi={googleApi.maps} // Pass google.maps specifically*/}
            {/*        mapTypes={mapTypesForSwitcher}*/}
            {/*        initialMapTypeId={googleApi.maps.MapTypeId.ROADMAP}*/}
            {/*        onStyleChange={(switching) => setIsSwitchingStyle(switching)}*/}
            {/*    />*/}
            {/*)}*/}
            {overallReady && children && React.Children.map(children, child =>
                React.isValidElement(child) ? React.cloneElement(child, {
                    map: mapInstanceShared,
                    googleMapsApi: googleApi.maps, // Pass google.maps
                    icons: preparedIcons
                }) : child
            )}
            {!overallReady && (
                <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000, color: 'black', backgroundColor: 'rgba(255,255,255,0.8)', padding: '10px', borderRadius: '5px'}}>
                    Loading Map...
                </div>
            )}
        </div>
    );
};

export default MapGoogle;